/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React from 'react';
import { Box, Dialog, IconButton, Typography, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { McpInstallPanel } from '../mcpConnect/McpInstallPanel';
import { ExperimentalChip, LocalChip } from './McpServerCard';
import type { McpCatalogEntry } from './entries';
import { MORPH_SURFACE, MORPH_TITLE } from './viewTransition';

const useStyles = makeStyles(theme => ({
  '@global': {
    [`::view-transition-group(${MORPH_SURFACE})`]: {
      animationDuration: '360ms',
      animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
      overflow: 'clip',
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
    },
    [`::view-transition-group(${MORPH_TITLE})`]: {
      animationDuration: '360ms',
      animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    },
    [`::view-transition-old(${MORPH_SURFACE}), ::view-transition-new(${MORPH_SURFACE})`]: {
      height: '100%',
      width: '100%',
      objectFit: 'none',
      objectPosition: 'left top',
    },
    [`::view-transition-old(${MORPH_SURFACE})`]: {
      animationDuration: '120ms',
      animationTimingFunction: 'ease-out',
    },
    [`::view-transition-new(${MORPH_SURFACE})`]: {
      animationDuration: '240ms',
      animationDelay: '80ms',
      animationTimingFunction: 'ease-out',
      animationFillMode: 'both',
    },
    '::view-transition-old(root), ::view-transition-new(root)': { animationDuration: '240ms' },
  },
  paper: {
    borderRadius: 12,
    padding: theme.spacing(3),
    backgroundImage: 'none',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) },
  },
  backdrop: {
    backdropFilter: 'blur(6px)',
    backgroundColor: alpha(theme.palette.common.black, 0.5),
  },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1) },
  headSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    paddingBottom: theme.spacing(1),
  },
  heading: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
  meta: { fontSize: '0.8125rem', color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  links: { marginTop: theme.spacing(2), fontSize: '0.8125rem' },
}));

export const McpConnectDialog = ({ entry, morphed, client, onClose, onEdit, onExited }: {
  entry: McpCatalogEntry | null;
  morphed: boolean;
  /** The page's "Works with" filter, so the panel opens on that client. */
  client?: string | null;
  onClose: () => void;
  onEdit?: () => void;
  onExited?: () => void;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const entityRoute = useRouteRef(entityRouteRef);
  const name = entry?.descriptor.name;
  const closeRef = React.useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={entry !== null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="mcp-dialog-title"
      transitionDuration={morphed ? 0 : undefined}
      TransitionProps={{ onEntered: () => closeRef.current?.focus(), onExited }}
      classes={{ paper: classes.paper }}
      PaperProps={{ 'aria-modal': true, style: entry ? { viewTransitionName: MORPH_SURFACE } : undefined } as object}
      BackdropProps={{ className: classes.backdrop }}
    >
      {entry && (
        <>
          <Box className={`${classes.head}${fullScreen ? ` ${classes.headSticky}` : ''}`}>
            <Box>
              <Box className={classes.heading}>
                <Typography component="h2" variant="h5" id="mcp-dialog-title" style={{ viewTransitionName: MORPH_TITLE }}>
                  {entry.descriptor.title}
                </Typography>
                {entry.descriptor.transport.kind === 'local' && <LocalChip />}
                {entry.experimental && <ExperimentalChip />}
              </Box>
              <Typography className={classes.meta}>
                by {entry.provider}{entry.owner ? ` · owned by ${entry.owner}` : ''}
              </Typography>
              {entry.experimental && (
                <Typography className={classes.meta}>
                  Experimental: at least one listed client needed a workaround to pass testing.
                </Typography>
              )}
            </Box>
            <Box display="flex" style={{ gap: 4 }} className={fullScreen ? classes.headSticky : undefined}>
              {onEdit && (
                <IconButton size="small" aria-label={`Edit ${entry.descriptor.title}`} onClick={onEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton ref={closeRef} size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
          <McpInstallPanel
            key={name}
            server={entry.descriptor}
            idPrefix={`mcp-${name}`}
            initialClient={client ?? undefined}
            analytics={{
              copy: `mcp-catalog-copy:${name}`,
              switch: `mcp-catalog-switch:${name}`,
              variant: `mcp-catalog-variant:${name}`,
              connect: `mcp-catalog-connect:${name}`,
            }}
          />
          <Box className={classes.links}>
            <Link to={entityRoute(parseEntityRef(entry.entityRef))}>Catalog entry</Link>
          </Box>
        </>
      )}
    </Dialog>
  );
};
